/**
 * DeviceMonitoringReveal — audited reveal of a device's collected SMS + call
 * logs (UPGRADE-03 Phase 4). The records are AES-256-GCM sealed at rest; the
 * backend decrypts them ONLY on these `device.monitoring.view`-gated endpoints,
 * and every reveal is written to the audit chain (count-only).
 *
 * SMS reveals inline here as a flat, newest-first table and pages back through
 * older messages ("Load older messages") via the keyset-paginated endpoint; a
 * link opens the dedicated conversation view (`/devices/:id/sms`) for a
 * threaded, chat-style read of the same data. Calls reveal inline.
 *
 * This may be a child's message content — gated client- and server-side on
 * `device.monitoring.view`, and the banner reminds the operator each view is
 * logged.
 */
import { useState } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Badge, type BadgeTone } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { FormError, apiErrorMessage } from "./Form.js";
import { Table, TBody, THead, Th, Td, Tr } from "./Table.js";
import { api } from "../lib/api.js";
import { formatDateTime } from "../lib/utils.js";

interface SmsLogView {
  readonly id: string;
  readonly direction: string;
  readonly address: string;
  readonly body: string;
  readonly sentAt: string;
}
interface SmsLogsPage {
  readonly deviceId: string;
  readonly count: number;
  readonly returned: number;
  readonly records: readonly SmsLogView[];
  readonly nextCursor: string | null;
}
interface CallLogView {
  readonly id: string;
  readonly direction: string;
  readonly number: string;
  readonly durationSec: number;
  readonly occurredAt: string;
}
interface CallLogsView {
  readonly deviceId: string;
  readonly count: number;
  readonly records: readonly CallLogView[];
}

const SMS_PAGE_SIZE = 200;

function directionTone(direction: string): BadgeTone {
  const d = direction.toLowerCase();
  if (d === "outbound") return "brand";
  if (d === "inbound") return "neutral";
  if (d === "missed" || d === "rejected" || d === "blocked") return "danger";
  return "neutral";
}

function formatDuration(sec: number): string {
  if (sec <= 0) return "0s";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function DeviceMonitoringReveal({ deviceId }: { readonly deviceId: string }): JSX.Element {
  const [tab, setTab] = useState<"sms" | "calls">("sms");
  const [smsRevealed, setSmsRevealed] = useState(false);

  const sms = useInfiniteQuery({
    queryKey: ["devices", "smsLogs", deviceId],
    queryFn: ({ pageParam }) =>
      api.get<SmsLogsPage>(
        `/api/devices/${deviceId}/sms-logs?limit=${SMS_PAGE_SIZE}` +
          (pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: smsRevealed,
  });
  const smsRecords = sms.data?.pages.flatMap((p) => p.records) ?? [];
  const smsTotal = sms.data?.pages[0]?.count ?? 0;

  const calls = useMutation({
    mutationFn: () => api.get<CallLogsView>(`/api/devices/${deviceId}/call-logs`),
    onError: () => {},
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monitoring data (SMS &amp; calls)</CardTitle>
        <Badge tone="warning">audited reveal</Badge>
      </CardHeader>
      <CardBody>
        <p className="mb-3 text-xs text-simuflex-slate">
          SMS bodies and call numbers are encrypted at rest and decrypted only here, under the{" "}
          <code>device.monitoring.view</code> permission.{" "}
          <strong>Every reveal is written to the audit log.</strong> This may be a child&apos;s
          message content — view only when necessary.
        </p>

        <div className="mb-4 inline-flex gap-2">
          <Button
            size="sm"
            variant={tab === "sms" ? "primary" : "secondary"}
            disabled={sms.isFetching && tab === "sms"}
            onClick={() => {
              setTab("sms");
              setSmsRevealed(true);
            }}
          >
            {sms.isFetching && tab === "sms" && !smsRecords.length ? "Revealing…" : "Reveal SMS"}
          </Button>
          <Button
            size="sm"
            variant={tab === "calls" ? "primary" : "secondary"}
            disabled={calls.isPending}
            onClick={() => {
              setTab("calls");
              calls.mutate();
            }}
          >
            {calls.isPending ? "Revealing…" : "Reveal calls"}
          </Button>
        </div>

        {tab === "sms" ? (
          <>
            <FormError message={sms.isError ? apiErrorMessage(sms.error) : null} />
            {smsRevealed ? (
              smsRecords.length === 0 && !sms.isFetching ? (
                <p className="text-sm text-simuflex-slate">No SMS collected for this device yet.</p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-simuflex-slate">
                      {smsRecords.length} of {smsTotal} message(s) loaded
                    </p>
                    <Link
                      to="/devices/$id/sms"
                      params={{ id: deviceId }}
                      className="text-xs font-medium text-simuflex-green hover:underline"
                    >
                      Open conversation view →
                    </Link>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <THead>
                        <Tr>
                          <Th>When</Th>
                          <Th>Direction</Th>
                          <Th>Number</Th>
                          <Th>Message</Th>
                        </Tr>
                      </THead>
                      <TBody>
                        {smsRecords.map((r) => (
                          <Tr key={r.id}>
                            <Td className="whitespace-nowrap text-xs text-simuflex-slate">
                              {formatDateTime(r.sentAt)}
                            </Td>
                            <Td>
                              <Badge tone={directionTone(r.direction)}>{r.direction}</Badge>
                            </Td>
                            <Td className="font-mono text-xs">{r.address}</Td>
                            <Td className="text-xs">{r.body}</Td>
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                  {sms.hasNextPage ? (
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={sms.isFetchingNextPage}
                        onClick={() => void sms.fetchNextPage()}
                      >
                        {sms.isFetchingNextPage ? "Loading…" : "Load older messages"}
                      </Button>
                    </div>
                  ) : null}
                </>
              )
            ) : (
              <p className="text-sm text-simuflex-slate">Click “Reveal SMS” to decrypt and view.</p>
            )}
          </>
        ) : (
          <>
            <FormError message={calls.isError ? apiErrorMessage(calls.error) : null} />
            {calls.data ? (
              calls.data.records.length === 0 ? (
                <p className="text-sm text-simuflex-slate">
                  No calls collected for this device yet.
                </p>
              ) : (
                <>
                  <p className="mb-2 text-xs text-simuflex-slate">{calls.data.count} call(s)</p>
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <THead>
                        <Tr>
                          <Th>When</Th>
                          <Th>Direction</Th>
                          <Th>Number</Th>
                          <Th>Duration</Th>
                        </Tr>
                      </THead>
                      <TBody>
                        {calls.data.records.map((r) => (
                          <Tr key={r.id}>
                            <Td className="whitespace-nowrap text-xs text-simuflex-slate">
                              {formatDateTime(r.occurredAt)}
                            </Td>
                            <Td>
                              <Badge tone={directionTone(r.direction)}>{r.direction}</Badge>
                            </Td>
                            <Td className="font-mono text-xs">{r.number}</Td>
                            <Td className="text-xs">{formatDuration(r.durationSec)}</Td>
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                </>
              )
            ) : !calls.isPending && !calls.isError ? (
              <p className="text-sm text-simuflex-slate">
                Click “Reveal calls” to decrypt and view.
              </p>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
